import type { JSX } from 'react';
import * as Switch from '@radix-ui/react-switch';
import * as RadioGroup from '@radix-ui/react-radio-group';
import { useTranslation } from 'react-i18next';
import type { TimerPreset } from '@susdle/shared';
import './HostControls.css';

interface HostControlsProps {
  isHost: boolean;
  foolEnabled: boolean;
  timerPreset: TimerPreset;
  onFoolChange: (enabled: boolean) => void;
  onTimerChange: (preset: TimerPreset) => void;
}

/**
 * Each knob is a tiny printed entry on the verso, structured exactly
 * like the recto's PrintedCast across the spine: an italic-small-caps
 * label above an italic Garamond value line. The chosen option lifts
 * to ink with a 2px under-rule (the printer's chosen-mark); the rest
 * sit in ash-soft. Verso typography rhymes with the recto's printed
 * cast line — two pages of the same chapbook speaking one voice.
 * Begin lives in SignatureBand at the page's signature line.
 */
export function HostControls(props: HostControlsProps): JSX.Element {
  const { t } = useTranslation();
  const disabled = !props.isHost;

  return (
    <div
      className="susdle-host-controls"
      data-disabled={disabled ? 'yes' : 'no'}
      aria-disabled={disabled || undefined}
    >
      <div className="susdle-host-controls__row">
        {/* Fool knob. Switch.Root is one button; clicking either option-
            word flips state. The middle-dot between is visual-only. */}
        <div className="susdle-knob">
          <span className="susdle-knob__label">{t('lobby.host.fool.label')}</span>
          <Switch.Root
            className="susdle-knob__values susdle-knob__values--binary"
            checked={props.foolEnabled}
            onCheckedChange={props.onFoolChange}
            disabled={disabled}
            aria-label={t('lobby.host.fool.label')}
          >
            <span
              className="susdle-knob__opt"
              data-state={props.foolEnabled ? 'unchecked' : 'checked'}
              aria-hidden="true"
            >
              {t('lobby.host.fool.off')}
            </span>
            <span className="susdle-knob__sep" aria-hidden="true">·</span>
            <span
              className="susdle-knob__opt"
              data-state={props.foolEnabled ? 'checked' : 'unchecked'}
              aria-hidden="true"
            >
              {t('lobby.host.fool.on')}
            </span>
          </Switch.Root>
        </div>

        {/* Timer knob. Each option is a RadioGroup.Item; inert middle-dot
            spans sit between them so the dot isn't part of any option's
            click target. */}
        <div className="susdle-knob">
          <span className="susdle-knob__label">{t('lobby.host.timer.label')}</span>
          <RadioGroup.Root
            className="susdle-knob__values susdle-knob__values--series"
            value={props.timerPreset}
            onValueChange={(v) => props.onTimerChange(v as TimerPreset)}
            aria-label={t('lobby.host.timer.label')}
            disabled={disabled}
          >
            <TimerOption value="slow"     label={t('lobby.host.timer.slow')} />
            <span className="susdle-knob__sep" aria-hidden="true">·</span>
            <TimerOption value="standard" label={t('lobby.host.timer.standard')} />
            <span className="susdle-knob__sep" aria-hidden="true">·</span>
            <TimerOption value="quick"    label={t('lobby.host.timer.quick')} />
          </RadioGroup.Root>
        </div>
      </div>
    </div>
  );
}

function TimerOption({ value, label }: { value: TimerPreset; label: string }): JSX.Element {
  return (
    <RadioGroup.Item className="susdle-knob__opt" value={value} id={`timer-${value}`}>
      {label}
    </RadioGroup.Item>
  );
}
